import { type FieldMessage } from "ui/forms/field-message/field-message.types";

export type FieldMessageTone = "error" | "helperText" | "warning";

const ID_SUFFIX: Record<FieldMessageTone, string> = {
  error: "error",
  helperText: "helper-text",
  warning: "warning",
};

export type FieldMessages = {
  errorMessage?: FieldMessage;
  helperText?: FieldMessage;
  warningMessage?: FieldMessage;
};

export const fieldMessageId = (name: string | undefined, tone: FieldMessageTone) =>
  name ? `${name}-${ID_SUFFIX[tone]}` : undefined;

/** An error replaces helper copy rather than stacking with it, so the two are never both shown. */
export const visibleFieldMessages = ({
  errorMessage,
  helperText,
  warningMessage,
}: FieldMessages): Record<FieldMessageTone, boolean> => ({
  error: !!errorMessage,
  helperText: !errorMessage && !!helperText,
  warning: !!warningMessage,
});

/**
 * The `aria-describedby` a control needs for every message under it to be announced. Derived from
 * the same visibility rule the messages render by, so the attribute can neither point at an element
 * that is not there nor miss one that is — the gap that left helper and warning copy silent while
 * only the error was wired.
 */
export const fieldDescribedBy = (name: string | undefined, messages: FieldMessages) => {
  const visible = visibleFieldMessages(messages);
  const ids = (Object.keys(visible) as FieldMessageTone[])
    .filter((tone) => visible[tone])
    .map((tone) => fieldMessageId(name, tone))
    .filter(Boolean);

  return ids.length ? ids.join(" ") : undefined;
};
